/**
 * MemoryCard
 *
 * Shared card component for displaying and managing AI therapist memory.
 * Used by both SelfMemoryPanel (account page) and MemoryPanel (partner detail page).
 *
 * Responsibilities:
 * - Renders card with brain icon, configurable title, last-updated timestamps
 * - Displays markdown-formatted memory content
 * - Action buttons: Edit, Reset to AI version (when user_has_edited), Regenerate, Clear
 * - Loading spinners per-button
 * - Confirmation dialogs for Reset, Regenerate, Clear (managed internally)
 */

import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Brain,
  Pencil,
  RotateCcw,
  RefreshCw,
  Trash2,
  Loader2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import type { SpeakerMemory } from "../../types/speakerMemory";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export interface MemoryCardProps {
  title: string;
  memory: SpeakerMemory;
  onEdit: () => void;
  onReset: () => Promise<void>;
  onRegenerate: () => Promise<void>;
  onClear: () => Promise<void>;
  resetPending?: boolean;
  regeneratePending?: boolean;
  clearPending?: boolean;
}

export function MemoryCard({
  title,
  memory,
  onEdit,
  onReset,
  onRegenerate,
  onClear,
  resetPending = false,
  regeneratePending = false,
  clearPending = false,
}: MemoryCardProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const handleReset = async () => {
    await onReset();
    setShowResetConfirm(false);
  };

  const handleRegenerate = async () => {
    await onRegenerate();
    setShowRegenerateConfirm(false);
  };

  const handleClear = async () => {
    await onClear();
    setShowClearConfirm(false);
  };

  return (
    <>
      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        </div>

        <div className="mb-4 space-y-1 text-sm text-muted-foreground">
          <p>Last updated by AI: {formatDate(memory.last_ai_updated_at)}</p>
          {memory.user_has_edited && memory.last_user_edited_at && (
            <p className="text-amber-600 dark:text-amber-400">
              You edited this on {formatDate(memory.last_user_edited_at)}
            </p>
          )}
        </div>

        <div className="prose prose-sm max-w-none text-foreground [&>h1]:text-base [&>h1]:font-semibold [&>h2]:text-sm [&>h2]:font-semibold [&>h3]:text-sm [&>h3]:font-medium [&>ul]:space-y-1 [&>p]:leading-relaxed">
          <ReactMarkdown>{memory.content}</ReactMarkdown>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
          {memory.user_has_edited && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              disabled={resetPending}
            >
              {resetPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Reset to AI version
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRegenerateConfirm(true)}
            disabled={regeneratePending}
          >
            {regeneratePending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Regenerate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClearConfirm(true)}
            disabled={clearPending}
            className="text-destructive hover:text-destructive"
          >
            {clearPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            Clear memory
          </Button>
        </div>
      </Card>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to AI version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will discard your edits and restore the AI-generated memory.
              The therapist will continue updating it based on future
              reflections.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset}>
              {resetPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear memory?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This will erase the therapist&apos;s observations.
              They will be rebuilt after your next reflection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showRegenerateConfirm}
        onOpenChange={setShowRegenerateConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate memory?</AlertDialogTitle>
            <AlertDialogDescription>
              This wipes the current memory and rebuilds it from your 10 most
              recent reflections. Your edits will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate}>
              {regeneratePending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
